import React from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import moment from 'moment';

import { formatPrice } from 'common/formatters';
import { getListingFieldsForDisplay } from 'common/listingHelpers';
import { isPreLeaseActive } from 'common/preLeaseHelpers';
import { calculateNetEffectiveRent } from 'common/concessionHelpers';
import {
  LISTING_DISPLAY_TYPE_MULTI_UNIT,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrPropertyTypeLabel from 'client/components/SrPropertyTypeLabel';

const SrListingFeature = ({ value, title }) => (
  <SrText>
    <SrText weight="bold">{`${value} `}</SrText>
    <SrText>{title}</SrText>
  </SrText>
);

const SrListingPriceBlock = ({ listing, marketingListing, preLease, concession }) => {
  const listingFieldsForDisplay = getListingFieldsForDisplay({ listing });
  const addressSuffix = listing.addressStreetSuffix ? listing.addressStreetSuffix + ' ' : '';
  const addressUnit = listing.apartmentUnit ? listing.apartmentUnit + ' ' : '';
  const displayComingSoonTag = preLease && isPreLeaseActive({ preLease });
  const priceToAdvertise = concession?.advertiseNER
    ? calculateNetEffectiveRent({
        concession: concession,
        rentPrice: listing.price,
      }).netEffectiveRent
    : listingFieldsForDisplay.price;

  return (
    <SrBox>
      <SrBox>
        <SrBox marginRight="14px" display="inlineBlock">
          <SrText size="2xl" weight="bold">
            {formatPrice(priceToAdvertise)}
          </SrText>
          {!!concession?.advertiseNER && (
            <SrText
              size="21"
              weight="500"
              color="greyMonochrome"
              decoration="line-through"
              paddingLeft="10px"
            >
              {formatPrice(listingFieldsForDisplay.price)}
            </SrText>
          )}
        </SrBox>
        {listing.displayType !== LISTING_DISPLAY_TYPE_MULTI_UNIT && (
          <SrText>
            <SrListingFeature value={listing.bedrooms} title="bed" />
            <SrText className="pipe"> | </SrText>
            <SrListingFeature value={listing.bathrooms} title="bath" />
            <SrText>
              <SrText className="pipe"> | </SrText>
              <SrListingFeature value={listing.squareFeet} title="sqft" />
            </SrText>
          </SrText>
        )}
      </SrBox>
      {listing.apartmentUnits && listing.apartmentUnits.length > 0 && (
        <SrBox marginTop={8}>
          <SrText>
            {`${pluralize('unit layout', listing.apartmentUnits.length, true)} listed below`}
          </SrText>
        </SrBox>
      )}
      <SrBox marginTop={5}>
        <SrText weight="medium">
          {listing.dataSource !== LISTING_DATA_SOURCE_SMART_APARTMENT &&
            (listing.displayType === LISTING_DISPLAY_TYPE_MULTI_UNIT
              ? listing.apartmentName
              : listing.dataSource === LISTING_DATA_SOURCE_SUNROOM
              ? listing.addressStreetNumber +
                ' ' +
                listing.addressStreetName +
                ' ' +
                addressSuffix +
                addressUnit
              : listing.addressStreetDisplay)}
          <SrText color="grey">
            {' ' + listing.addressCity + ', ' + listing.addressStateCode + ' ' + listing.addressZip}
          </SrText>
        </SrText>
      </SrBox>
      <SrText paddingTop="12px">
        <SrPropertyTypeLabel propertyType={listing.propertyType} isCircular />
        {displayComingSoonTag && (
          <>
            <SrText paddingLeft="10px" />
            <SrText className="SrPropertyTypeLabel isCircular preLease" weight="700">
              Coming Soon:{' '}
              {[
                moment(preLease?.startDate).format('MMM D'),
                preLease?.completedDate
                  ? moment(marketingListing?.dueDate).format('MMM D')
                  : moment(preLease?.endDate).format('MMM D'),
              ].join(' - ')}
            </SrText>
          </>
        )}
      </SrText>
    </SrBox>
  );
};

SrListingPriceBlock.propTypes = {
  listing: PropTypes.object.isRequired,
  marketingListing: PropTypes.object,
  preLease: PropTypes.object,
  concession: PropTypes.object,
};

SrListingPriceBlock.displayName = 'SrListingPriceBlock';

export default SrListingPriceBlock;
